import { useState, useEffect, useCallback } from 'react';

import { api } from '@/services/api-class';

// ── Types ────────────────────────────────────────────────────────────

interface ResetPasswordErrors {
  password: string;
  confirmPassword: string;
  general: string;
}

const emptyErrors: ResetPasswordErrors = {
  password: '',
  confirmPassword: '',
  general: '',
};

// ── Component ────────────────────────────────────────────────────────

interface ResetPasswordScreenProps {
  code?: string;
}

export function ResetPasswordScreen({ code }: ResetPasswordScreenProps = {}) {
  const [resetCode] = useState<string | null>(() => {
    if (code) return code;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('code');
  });

  // Form fields
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Validation
  const [errors, setErrors] = useState<ResetPasswordErrors>({ ...emptyErrors });
  const [codeValid, setCodeValid] = useState<boolean | null>(null);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Validate reset code on mount
  useEffect(() => {
    if (!resetCode) {
      setCodeValid(false);
      setErrors((prev) => ({ ...prev, general: 'No reset code provided. Please use the link from your email.' }));
      return;
    }

    api.validatePasswordResetCode(resetCode, (result: any) => {
      if (result === false || (result && result.error)) {
        setCodeValid(false);
        setErrors((prev) => ({ ...prev, general: 'Invalid or expired reset code. Please request a new password reset.' }));
      } else {
        setCodeValid(true);
      }
    });
  }, [resetCode]);

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      if (e) e.preventDefault();

      let hasErrors = false;
      const newErrors = { ...emptyErrors };

      if (!password) {
        newErrors.password = 'Password is required.';
        hasErrors = true;
      }

      if (!confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password.';
        hasErrors = true;
      }

      if (password && confirmPassword && password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match.';
        hasErrors = true;
      }

      if (hasErrors) {
        setErrors(newErrors);
        return;
      }

      setSubmitting(true);
      setErrors({ ...emptyErrors });

      const data = {
        resetCode,
        newPassword1: password,
        newPassword2: confirmPassword,
      };

      api.resetPassword(
        JSON.stringify(data),
        () => {
          setSubmitting(false);
          setShowSuccessModal(true);
        },
        () => {
          setSubmitting(false);
          setErrors((prev) => ({
            ...prev,
            general: 'Error updating password. Please try again.',
          }));
        }
      );
    },
    [password, confirmPassword, resetCode]
  );

  const redirectToLogin = useCallback(() => {
    setRedirecting(true);
    window.location.replace('/');
  }, []);

  return (
    <div className="kst-registration-page">
      {/* Background Panel */}
      <div
        className="kst-registration-bg"
        style={{ backgroundImage: "url('images/bg-01.jpg')" }}
      >
        <span className="kst-browser-label">
          Supported browsers: Edge, Safari, Chrome
        </span>
      </div>

      {/* Form Panel */}
      <div className="kst-registration-form-panel">
        <form className="kst-registration-form" onSubmit={handleSubmit}>
          <h1 className="kst-registration-title">Reset Password</h1>

          {/* General Error */}
          {errors.general && (
            <div className="kst-reg-error kst-reg-error--general">
              {errors.general}
            </div>
          )}

          {/* Password */}
          <div className="kst-reg-input-group">
            <label className="kst-reg-label">Password</label>
            <input
              type="password"
              className={`kst-reg-input${
                errors.password ? ' kst-reg-input--error' : ''
              }`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Type Password here..."
              disabled={codeValid === false}
              autoComplete="new-password"
            />
            {errors.password && (
              <div className="kst-reg-error">{errors.password}</div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="kst-reg-input-group">
            <label className="kst-reg-label">Re-Type Password</label>
            <input
              type="password"
              className={`kst-reg-input${
                errors.confirmPassword ? ' kst-reg-input--error' : ''
              }`}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-Type Password here..."
              disabled={codeValid === false}
              autoComplete="new-password"
            />
            {errors.confirmPassword && (
              <div className="kst-reg-error">{errors.confirmPassword}</div>
            )}
          </div>

          {/* Submit Button */}
          <div className="kst-reg-submit-container">
            <button
              type="submit"
              className="kst-reg-submit-btn"
              disabled={submitting || codeValid === false}
            >
              {submitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="kst-reg-success-overlay" onClick={(e) => { if (e.target === e.currentTarget) redirectToLogin(); }}>
          <div className="kst-reg-success-modal">
            <h2 className="kst-reg-success-title">Password Reset Successful</h2>
            <p className="kst-reg-success-message">
              Please click the &quot;Sign In&quot; button below to go to the sign in screen.
            </p>
            <button
              type="button"
              className="kst-reg-success-btn"
              disabled={redirecting}
              onClick={redirectToLogin}
            >
              {redirecting ? 'Redirecting...' : 'Sign In'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ResetPasswordScreen;
